import { apiClient } from "./health";
import type { JoybuyPromotionDTO, JoybuyPromotionRowDTO, NeoEnvironmentDTO } from "./generated/react-contract";
export type { NeoEnvironmentDTO } from "./generated/react-contract";

export type NeoNumeric = string | number | null;
export type PeriodGrain = "YEAR" | "QUARTER" | "MONTH";
export type ChannelPeriodGrain = PeriodGrain | "WEEK";

export interface SalesContributionDetailDTO {
  key: string;
  customer_key: string;
  customer_label: string;
  model_key: string;
  model_label: string;
  actual_units: NeoNumeric;
  actual_revenue: NeoNumeric;
  target_units: null;
  target_revenue: null;
  units_completion: null;
  revenue_completion: null;
  units_share_within_customer: NeoNumeric;
  units_share_within_model: NeoNumeric;
  revenue_share_within_customer: NeoNumeric;
  revenue_share_within_model: NeoNumeric;
  customer_target_units: NeoNumeric;
  customer_target_revenue: NeoNumeric;
  customer_units_completion: NeoNumeric;
  customer_revenue_completion: NeoNumeric;
  source_row_count: number;
}

export interface SalesContributionCategoryDTO {
  key: string;
  label: string;
  actual_units: NeoNumeric;
  actual_revenue: NeoNumeric;
  target_units: NeoNumeric;
  target_revenue: NeoNumeric;
  units_completion: NeoNumeric;
  revenue_completion: NeoNumeric;
  units_gap: NeoNumeric;
  revenue_gap: NeoNumeric;
  segments: SalesContributionDetailDTO[];
}

export interface SalesContributionVisualizationDTO {
  authority: string;
  customer_dimension: "CANONICAL_CUSTOMER" | "AMAZON_COUNTRY_MARKETPLACE_CONTEXT";
  target_grain: "CUSTOMER_PERIOD" | "NOT_AVAILABLE";
  model_target_rule: "EXACT_MODEL_TARGET_REQUIRED_NO_ALLOCATION";
  missing_semantics: "MISSING_IS_NOT_ZERO";
  model_chart: SalesContributionCategoryDTO[];
  customer_chart: SalesContributionCategoryDTO[];
  detail_rows: SalesContributionDetailDTO[];
}

export interface NeoGlobalCustomerDTO {
  customer_id: string;
  customer_code: string | null;
  customer_name: string | null;
  countries: string[];
  channels: string[];
  sales_units: NeoNumeric;
  revenue_by_currency: Record<string, NeoNumeric>;
  revenue: NeoNumeric;
  currency: string | null;
  revenue_status: "AVAILABLE" | "PARTIAL_REVENUE" | "MULTI_CURRENCY" | "MISSING";
  target_units: NeoNumeric;
  units_variance: NeoNumeric;
  units_completion: NeoNumeric;
  target_revenue: NeoNumeric;
  actual_revenue_eur: NeoNumeric;
  gross_invoice_revenue_eur: NeoNumeric;
  management_deduction_eur: NeoNumeric;
  management_rate: NeoNumeric;
  management_rule_evidence: string[];
  revenue_completion: NeoNumeric;
  actual_revenue_status: string;
  target_revenue_status: string;
  target_currency: string | null;
  target_status: string;
  target_state: "TARGET_MET" | "BELOW_TARGET" | "MISSING_TARGET";
  standard_customer_scope_status: "IN_STANDARD_CUSTOMER_SCOPE" | "OUT_OF_STANDARD_CUSTOMER_SCOPE" | "CUSTOMER_SCOPE_UNRESOLVED";
  remaining_gap: NeoNumeric;
  remaining_target_units: NeoNumeric;
  remaining_target_revenue_eur: NeoNumeric;
  rn_units?: NeoNumeric;
  rn_gross_sell_in_revenue_eur?: NeoNumeric;
  rn_contract_rebate_eur?: NeoNumeric;
  rn_contract_rebate_percent?: NeoNumeric;
  rn_marketing_support_eur?: NeoNumeric;
  rn_marketing_support_percent?: NeoNumeric;
  rn_net_sell_in_revenue_eur?: NeoNumeric;
  rn_commercial_coverage?: string;
  rn_deduction_grain?: string;
  rn_commercial_authority?: string[];
  rn_target_completion?: NeoNumeric;
  rn_remaining_target_units?: NeoNumeric;
  rn_net_revenue_completion?: NeoNumeric;
  rn_remaining_net_revenue_target_eur?: NeoNumeric;
  invoice_units?: NeoNumeric;
  invoice_target_completion?: NeoNumeric;
  invoice_remaining_target_units?: NeoNumeric;
  invoice_net_revenue_completion?: NeoNumeric;
  invoice_remaining_net_revenue_target_eur?: NeoNumeric;
  matched_invoice_units?: NeoNumeric;
  open_rn_quantity?: NeoNumeric;
  rn_invoice_delta?: NeoNumeric;
  execution_status?: string;
  execution_matching_status?: string[];
  execution_details?: RNInvoiceExecutionDetailDTO[];
  target_revenue_basis?: "NET" | "UNRESOLVED";
  gross_sales_eur: NeoNumeric;
  gross_asp_eur: NeoNumeric;
  contract_rebate_eur: NeoNumeric;
  contract_rebate_percent: NeoNumeric;
  contract_rebate_status: string;
  marketing_support_eur: NeoNumeric;
  marketing_support_percent: NeoNumeric;
  marketing_support_status: string;
  total_commercial_deduction_eur: NeoNumeric;
  effective_deduction_percent: NeoNumeric;
  default_dynamic_rebate_eur?: NeoNumeric;
  item_rebate_eur?: NeoNumeric;
  item_rebate_status?: string;
  commercial_input_status?: string;
  net_sales_eur: NeoNumeric;
  net_sales_price_eur: NeoNumeric;
  net_sales_status: string;
  commercial_coverage: string;
  deduction_grain: string;
  commercial_authority: string[];
  source_identities: CustomerSourceIdentityDTO[];
  target_components: CustomerTargetComponentDTO[];
  country_breakdown: Array<{ country: string; sales_units: NeoNumeric; source_identity_count: number; invoice_line_count: number }>;
  period_trend: Array<{ period: string; actual_units: NeoNumeric; target_units: NeoNumeric; attainment_percent: NeoNumeric; remaining_gap: NeoNumeric; remaining_target_units: NeoNumeric; status: string }>;
}

export interface RNInvoiceExecutionDetailDTO {
  model_number?: string | null;
  customer_id: string | null;
  master_model_id: string | null;
  country: string | null;
  reference_type: string | null;
  reference: string | null;
  rn_fact_ids: string[];
  invoice_fact_ids: string[];
  rn_documents: string[];
  invoice_documents: string[];
  rn_business_months: string[];
  invoice_business_months: string[];
  rn_dates: string[];
  invoice_dates: string[];
  rn_quantity: NeoNumeric;
  matched_invoice_quantity: NeoNumeric;
  rn_invoice_delta: NeoNumeric;
  open_rn_quantity: NeoNumeric;
  execution_status: string;
  matching_status: string;
  review_reason?: string | null;
  raw_rn_customers?: string[];
  reconciliation_customer_id?: string | null;
  reconciliation_country?: string | null;
  cross_month: boolean;
}

export interface CustomerSourceIdentityDTO {
  source_customer_code: string | null;
  source_customer_name: string | null;
  invoice_legal_company_name: string | null;
  country: string | null;
  channel: string;
  invoice_document_count: number;
  invoice_line_count: number;
  sales_units: NeoNumeric;
  revenue_by_currency: Record<string, NeoNumeric>;
  invoice_numbers: string[];
  source_batch_ids: string[];
  inclusion_reason: string;
}

export interface CustomerTargetComponentDTO {
  source_customer_id: string;
  source_customer_code: string;
  source_customer_name: string | null;
  country: string | null;
  currency: string;
  monthly_target_units: Record<string, NeoNumeric>;
  monthly_target_revenue: Record<string, NeoNumeric>;
  source_lineage: string;
  resolution_status: string;
  mapping_ids: string[];
}

export interface OnlineRnSnapshotMetadata {
  snapshot_id: string;
  source_identity: {
    workbook_id: string;
    sheet: string;
    version: string;
    checksum_sha256: string;
  };
  fetched_at: string;
  accepted_at: string;
  as_of: string | null;
  freshness_status: "FRESH" | "STALE";
  age_seconds: number;
  refresh_interval_seconds: 600;
  stale_after_seconds: 900;
  last_refresh_failure: null | { at: string | null; code: string | null };
}

export interface NeoGlobalSalesDTO {
  customer_achievement: import("../components/CustomerAchievement").AchievementCustomer[];
  global_completion: {
    authority: string;
    missing_semantics: "MISSING_IS_NOT_ZERO";
    rn_known_actual: NeoNumeric;
    rn_coverage: "MISSING" | "PARTIAL" | "COMPLETE";
    rn_missing_quantity_rows: number;
    revenue: GlobalCompletionMetric;
    operational_units: GlobalCompletionMetric;
    financial_units: GlobalCompletionMetric;
  };
  sell_out_summary: {
    authority: string; status: string; period_start: string; period_end: string;
    customers_covered: number; units: NeoNumeric; models_covered: number | null;
    latest_period: string | null;
  };
  year: number;
  period_grain: PeriodGrain;
  selected_period: string;
  available_periods: string[];
  filters: { customer: string | null; country: string | null; model: string | null };
  filter_options: {
    customers: Array<{ value: string; label: string }>;
    countries: string[];
    models: Array<{ value: string; label: string }>;
  };
  target_filter_status: "SUPPORTED_CUSTOMER_GRAIN" | "TARGET_GRAIN_UNSUPPORTED_FOR_COUNTRY_OR_MODEL_FILTER";
  sales_units: NeoNumeric;
  invoice_line_count: number | null;
  execution_summary?: {
    rn_rows: number;
    invoiced_units: NeoNumeric;
    open_rn_units: NeoNumeric;
    reconciliation_review_units: NeoNumeric;
    model_resolved_units: NeoNumeric;
    model_unresolved_units: NeoNumeric;
    lifecycle_scope: {
      semantic: "CURRENT_ONLINE_RN_LIFECYCLE_SNAPSHOT";
      period: string;
      selected_reporting_period_independent: boolean;
    };
    snapshot: OnlineRnSnapshotMetadata | null;
    target_units: NeoNumeric;
    rn_units: NeoNumeric;
    rn_target_completion: NeoNumeric;
    rn_remaining_target_units: NeoNumeric;
    invoice_units: NeoNumeric;
    invoice_target_completion: NeoNumeric;
    invoice_remaining_target_units: NeoNumeric;
    net_revenue_target_eur: NeoNumeric;
    rn_net_revenue_completion: NeoNumeric;
    rn_remaining_net_revenue_target_eur: NeoNumeric;
    invoice_net_revenue_completion: NeoNumeric;
    invoice_remaining_net_revenue_target_eur: NeoNumeric;
    matched_invoice_units: NeoNumeric;
    open_rn_quantity: NeoNumeric;
    open_rn_coverage_status: "COMPLETE" | "PARTIAL" | "MISSING";
    unresolved_open_rn_lifecycles: number;
    rn_lifecycle_authority?: {
      aggregate_status: "COMPLETE" | "PARTIAL" | "MISSING";
      final_open_rn: NeoNumeric;
      known_open_rn: NeoNumeric;
      review_units: NeoNumeric;
      model_breakdown_status: "COMPLETE" | "PARTIAL" | "MISSING";
      model_resolved_open_rn: NeoNumeric;
      model_unresolved_open_rn: NeoNumeric;
      model_unresolved_open_rows: number;
    };
    rn_invoice_delta: NeoNumeric;
    execution_status: string;
    rn_gross_sell_in_revenue_eur: NeoNumeric;
    rn_contract_rebate_eur: NeoNumeric;
    rn_marketing_support_eur: NeoNumeric;
    rn_net_sell_in_revenue_eur: NeoNumeric;
    rn_commercial_coverage: string;
    invoice_net_sales_eur: NeoNumeric;
    invoice_nsp_eur: NeoNumeric;
    target_revenue_basis: "NET" | "UNRESOLVED";
    rn_customer_coverage: {
      total_rows: number;
      resolved_customer_rows: number;
      unresolved_customer_rows: number;
      quantity_missing_rows: number;
      total_units: NeoNumeric;
      resolved_customer_units: NeoNumeric;
      unresolved_customer_units: NeoNumeric;
      row_coverage_percent: NeoNumeric;
      unit_coverage_percent: NeoNumeric;
    };
    rn_source_coverage: string;
    invoice_source_coverage: string;
    rn_authority: string;
    invoice_authority: string;
    matching_authority: string;
    performance: {
      rn_source_row_count: number;
      invoice_source_row_count: number;
      lifecycle_count: number;
      matched_lifecycle_count: number;
      unresolved_lifecycle_count: number;
      query_duration_ms: number;
      matching_duration_ms: number;
      api_duration_ms: number;
    };
  };
  revenue_by_currency: Record<string, NeoNumeric>;
  revenue_summary: {
    gross_invoice_revenue_eur: NeoNumeric;
    management_deduction_eur: NeoNumeric;
    adjusted_sell_in_revenue_eur: NeoNumeric;
    management_rule_authority: string;
    management_rate_evidence: string[];
    actual_eur_normalized: NeoNumeric;
    covered_actual_eur_normalized: NeoNumeric;
    actual_value_eur: NeoNumeric;
    actual_value_status: "AVAILABLE" | "PARTIAL" | "MISSING";
    target_eur: NeoNumeric;
    covered_target_eur: NeoNumeric;
    completion_percent: NeoNumeric;
    reporting_currency: "EUR";
    fx_coverage_status: "COMPLETE" | "FX_INCOMPLETE" | "NOT_APPLICABLE_OUT_OF_SCOPE";
    target_coverage_status: "COMPLETE" | "PARTIAL_TARGET_COVERAGE" | "NOT_APPLICABLE_OUT_OF_SCOPE";
    fx_authority_status: string;
    fx_config_source: string;
    pln_to_eur_rate: NeoNumeric;
    pln_line_count: number;
    source_line_count: number | null;
    normalized_line_count: number | null;
    missing_revenue_line_count: number | null;
    free_of_charge_line_count: number | null;
    fx_required_line_count: number | null;
    target_row_count: number | null;
    covered_target_revenue_row_count: number | null;
    missing_target_revenue_count: number | null;
    gross_sales_eur: NeoNumeric;
    gross_asp_eur: NeoNumeric;
    contract_rebate_eur: NeoNumeric;
    contract_rebate_percent: NeoNumeric;
    contract_rebate_status: string;
    marketing_support_eur: NeoNumeric;
    marketing_support_percent: NeoNumeric;
    marketing_support_status: string;
    total_commercial_deduction_eur: NeoNumeric;
    effective_deduction_percent: NeoNumeric;
    net_sales_eur: NeoNumeric;
    net_sales_price_eur: NeoNumeric;
    net_sales_status: string;
    commercial_coverage: string;
    deduction_grain: string;
    commercial_authority: string[];
  };
  actual_business_coverage: {
    source_line_count: number | null;
    normalized_line_count: number | null;
    coverage_status: "COMPLETE" | "PARTIAL_REVENUE_COVERAGE";
    missing_revenue_line_count: number | null;
    free_of_charge_line_count: number | null;
    fx_required_line_count: number | null;
  };
  standard_customer_scope: {
    code: "STANDARD_CUSTOMER";
    label: "Standard Customer";
    included_customer_count: number | null;
    excluded_customer_count: number | null;
    excluded_customers: Array<{
      customer_id: string;
      customer_code: string | null;
      customer_name: string | null;
      status: "OUT_OF_STANDARD_CUSTOMER_SCOPE";
    }>;
    selected_in_scope_customer_count: number | null;
    filter_status: "STANDARD_CUSTOMER_SCOPE_ACTIVE" | "OUT_OF_SCOPE_CUSTOMER_SELECTED" | "TARGET_GRAIN_UNSUPPORTED";
    population_rule: string;
  };
  pln_evidence: Array<{
    invoice: string;
    invoice_line: string | null;
    customer: string | null;
    country: string | null;
    invoice_date: string;
    currency: "PLN";
    units: NeoNumeric;
    revenue_pln: NeoNumeric;
    revenue_eur_reporting: NeoNumeric;
    fx_status: string;
  }>;
  customers: NeoGlobalCustomerDTO[];
  contribution_visualization: SalesContributionVisualizationDTO;
  target_summary: {
    actual_customer_count: number;
    target_customer_count: number;
    missing_target_customer_count: number;
    coverage_status: "COMPLETE" | "PARTIAL_TARGET_COVERAGE" | "OUT_OF_STANDARD_CUSTOMER_SCOPE";
    total_target_units: NeoNumeric;
    actual_units: NeoNumeric;
    actual_status: "AVAILABLE" | "PARTIAL" | "MISSING";
    target_status: "AVAILABLE" | "PARTIAL" | "MISSING";
    completion_status: "AVAILABLE" | "PARTIAL" | "MISSING";
    covered_actual_units: NeoNumeric;
    covered_target_units: NeoNumeric;
    attainment_percent: NeoNumeric;
    remaining_gap: NeoNumeric;
    remaining_target_units: NeoNumeric;
    remaining_target_revenue_eur: NeoNumeric;
    aggregation_scope: string;
  };
  completion_pace: {
    effective_as_of_date: string;
    period_start_date: string | null;
    period_end_date: string | null;
    time_progress_percent: NeoNumeric;
    time_progress_status: "AVAILABLE" | "MISSING";
    completion_percent: NeoNumeric;
    completion_status: "AVAILABLE" | "PARTIAL" | "MISSING";
    pace_gap_percentage_points: NeoNumeric;
    pace_gap_status: "AVAILABLE" | "PARTIAL" | "MISSING";
    calendar_semantics: "INCLUSIVE_CALENDAR_DAYS";
    authority: "BACKEND_CALENDAR_REFERENCE_DATE";
  };
  trend: Array<{ period: string; customer_id: string; customer: string; sales_units: NeoNumeric }>;
  actual_target_trend: Array<{
    period: string;
    actual_units: NeoNumeric;
    target_units: NeoNumeric;
    units_completion: NeoNumeric;
    actual_revenue_eur: NeoNumeric;
    target_revenue_eur: NeoNumeric;
    revenue_completion: NeoNumeric;
    remaining_target_units?: NeoNumeric;
    remaining_target_revenue_eur?: NeoNumeric;
    target_coverage: "COMPLETE" | "PARTIAL_TARGET_COVERAGE" | "MISSING_TARGET" | "TARGET_GRAIN_UNSUPPORTED";
  }>;
  trend_grain: "MONTH";
  trend_scope: string;
  authority: string;
  amazon_po_contribution: NeoNumeric;
  rn_sales_contribution: NeoNumeric;
  operation_sales_contribution: NeoNumeric;
  missing_semantics: "MISSING_IS_NOT_ZERO";
  customer_identity_control: {
    raw_source_identity_count: number | null;
    canonical_customer_count: number | null;
    before_mapping_sales_units: NeoNumeric;
    after_mapping_sales_units: NeoNumeric;
    global_sales_total_drift: NeoNumeric;
    grouping_authority: "CANONICAL_CUSTOMER_WITH_MULTI_SOURCE_IDENTITIES";
    raw_invoice_evidence_immutable: true;
  };
}

export interface GlobalCompletionMetric {
  target: NeoNumeric;
  covered_target: NeoNumeric;
  actual: NeoNumeric;
  completion_percent: NeoNumeric;
  coverage_percent: NeoNumeric;
  target_customer_count: number;
  comparable_customer_count: number;
  missing_actual_customer_count: number;
  coverage_status: "MISSING" | "PARTIAL" | "COMPLETE";
}

export interface NeoProfitabilityAggregateDTO {
  key: string;
  dimensions: string[];
  currency: string | null;
  currency_status: "AVAILABLE" | "MULTI_CURRENCY" | "MISSING";
  source_row_count: number;
  complete_row_count: number;
  invoice_revenue: NeoNumeric;
  total_cm_rebate: NeoNumeric;
  contract_in_default_amount: NeoNumeric;
  contract_in_default_rate: NeoNumeric;
  contract_in_rate_authority: string | null;
  vfsd_deduction: NeoNumeric;
  vfsd_coverage: string;
  true_net_revenue: NeoNumeric;
  product_cost: NeoNumeric;
  weee: NeoNumeric;
  after_sales: NeoNumeric;
  logistics: NeoNumeric;
  contribution_margin: NeoNumeric;
  contribution_margin_rate: NeoNumeric;
  marketing_support: NeoNumeric;
  net_profit: NeoNumeric;
  covered_invoice_revenue: NeoNumeric;
  covered_total_cm_rebate: NeoNumeric;
  covered_true_net_revenue: NeoNumeric;
  covered_product_cost: NeoNumeric;
  covered_contribution_margin: NeoNumeric;
  rebate_coverage: string;
  coverage_status: "COMPLETE" | "PARTIAL" | "MISSING" | "NOT_COMPARABLE";
  net_sales?: NeoNumeric;
  covered_net_sales?: NeoNumeric;
  contract_rebate?: NeoNumeric;
  additional_contract_in_deduction?: NeoNumeric;
  total_models?: number;
  cost_covered_models?: number;
  total_lines?: number;
  covered_lines?: number;
  cost_covered_lines?: number;
  total_units?: NeoNumeric;
  covered_units?: NeoNumeric;
  coverage_percent?: NeoNumeric;
  exclusion_reasons?: string[];
}

export interface NeoProfitabilityDTO {
  summary?: Partial<NeoProfitabilityAggregateDTO>;
  formula_reconciliation?: { base: string; amazon: string };
  technical_evidence?: Array<{
    customer_name: string | null; customer_code: string; model_number: string | null;
    business_month: string; invoice_line_count: number; invoice_units: NeoNumeric;
    invoice_revenue: NeoNumeric; contract_rebate: NeoNumeric; marketing_support: NeoNumeric;
    net_sales: NeoNumeric; inventory_unit_cost: NeoNumeric; product_cost: NeoNumeric;
    vfsd_deduction: NeoNumeric; contribution_margin: NeoNumeric; cost_coverage: string;
    vfsd_coverage: string; rebate_coverage: string; lineage: string[]; formula_reconciliation: string;
  }>;
  technical_evidence_total?: number;
  unallocated_customer_deductions?: Array<{
    source_fact_id: string; customer_id: string; customer_name: string | null;
    deduction_type: string; period_start: string; period_end: string;
    source_amount: NeoNumeric; currency: string | null; source_evidence: string | null;
    source_grain: string; allocation_status: string; model_profitability_status: string;
  }>;
  year: number;
  period_grain: PeriodGrain;
  selected_period: string;
  available_periods: string[];
  channel_rows: NeoProfitabilityAggregateDTO[];
  product_rows: NeoProfitabilityAggregateDTO[];
  customer_drilldown_rows: NeoProfitabilityAggregateDTO[];
  source_row_count: number;
  revenue_authority: string;
  product_cost_authority: string;
  calculation_authority: string;
  coverage_rule: string;
  multi_currency_rule: string;
}

export interface NeoSalesTrendDTO {
  period: string;
  sales_units: NeoNumeric;
  revenue_by_currency: Record<string, NeoNumeric>;
  mom_percent: NeoNumeric;
  mom_status: string;
  yoy_percent: NeoNumeric;
  yoy_status: string;
}

export interface NeoChannelSalesDTO {
  channel: "AMAZON" | "JOYBUY";
  year: number;
  period_grain: ChannelPeriodGrain;
  selected_period: string;
  available_periods: string[];
  available_countries: string[];
  available_models: Array<{ master_model_id: string; model_number: string }>;
  selected_country: string | null;
  selected_model: string | null;
  week_options: Array<{ value: string; label: string; start: string | null; end: string }>;
  sales_actual_authority: "INVOICE_INVOICE_LINE";
  trend: NeoSalesTrendDTO[];
  model_ranking: Array<{ master_model_id: string; model_number: string; sales_units: NeoNumeric }>;
  operational_source_metrics: Array<Record<string, NeoNumeric | string>>;
  operational_metric_authority: string;
  mom_yoy_authority: string;
  sell_in: NeoChannelMetricDTO;
  sell_out: NeoChannelMetricDTO;
  daily_sellout: {
    contract_version: "BUSINESS_CONTRACT_AMAZON_DAILY_SELLOUT_V1";
    authority: "CURRENT_ACCEPTED_AMAZON_DAILY_SALES_SELLOUT";
    primary_source_code: "AMAZON_OPERATIONS_GOOGLE_DAILY_SALES";
    grain: ["business_date", "marketplace", "master_model_id"];
    status: "AVAILABLE" | "SOURCE_UNAVAILABLE";
    latest: { business_date: string | null; sellout_units: NeoNumeric; status: "AVAILABLE" | "SOURCE_UNAVAILABLE"; model_count: number };
    rows: Array<{ business_date: string; marketplace: string; country: string; master_model_id: string; model_number: string | null; sellout_units: NeoNumeric; value_status: "AVAILABLE" | "SOURCE_UNAVAILABLE"; asins: string[] }>;
    missing_policy: "MISSING_PRIMARY_IS_SOURCE_UNAVAILABLE_NEVER_ZERO";
    forbidden_fallbacks: string[];
    historical_legacy: { status: "DISABLED"; effective_date: null; rule: string };
  } | null;
  global_sales_actual_reference: { authority: "INVOICE_INVOICE_LINE"; series: NeoSalesTrendDTO[]; not_combined_with_channel_sources: true };
  metric_aggregation_rule: "SELL_IN_AND_SELL_OUT_ARE_NEVER_ADDED_TOGETHER";
  country_analytics: NeoChannelDimensionDTO[];
  model_analytics: NeoChannelDimensionDTO[];
  model_trends: Array<{ master_model_id: string; model_number: string; period: string; sell_in_units: NeoNumeric; sell_out_units: NeoNumeric; countries: string[] }>;
  contribution_visualization: SalesContributionVisualizationDTO;
  business_questions: string[];
}

export interface NeoChannelDimensionDTO {
  key: string;
  label: string;
  period: string;
  country?: string;
  master_model_id?: string;
  model_number?: string;
  countries?: string[];
  sell_in_units: NeoNumeric;
  sell_out_units: NeoNumeric;
  gap_units: NeoNumeric;
  sell_in_status: "AVAILABLE" | "MISSING";
  sell_out_status: "AVAILABLE" | "MISSING";
  source_row_count: number;
  sell_out_source_row_count: number;
  sell_out_revenue_by_currency: Record<string, NeoNumeric>;
  source_batch_count: number;
  resolved_product_rows: number;
  unresolved_product_rows: number;
  signed_fact_rows: number;
  zero_fact_rows: number;
  coverage_status: "COMPLETE" | "PARTIAL" | "MISSING";
  product_identifiers?: string[];
}

export interface NeoChannelMetricSeriesDTO {
  metric: "SELL_IN" | "SELL_OUT";
  period: string;
  country: string;
  master_model_id: string;
  model_number: string;
  source_batch_id: string | null;
  units: NeoNumeric;
  revenue: NeoNumeric;
  currency: string | null;
  currency_status: string;
  product_resolution: string;
  customer_resolution: string;
  source_row_count: number;
  product_identifiers?: string[];
  signed_fact_rows?: number;
  zero_fact_rows?: number;
}

export interface NeoChannelMetricDTO {
  authority: string;
  source_status: string;
  series: NeoChannelMetricSeriesDTO[];
  trend_period_summary: Array<{ period: string; units: NeoNumeric; status: "AVAILABLE" | "MISSING"; revenue_by_currency: Record<string, NeoNumeric>; mom_percent: NeoNumeric; mom_status: string; yoy_percent: NeoNumeric; yoy_status: string }>;
  period_summary: Array<{ period: string; units: NeoNumeric; status: "AVAILABLE" | "MISSING"; revenue_by_currency: Record<string, NeoNumeric>; mom_percent: NeoNumeric; mom_status: string; yoy_percent: NeoNumeric; yoy_status: string }>;
  coverage: { earliest_period: string | null; latest_period: string | null; populated_period_count: number; populated_periods: string[]; country_count: number; countries: string[]; model_count: number; source_batch_count: number; source_row_count: number; resolved_product_rows: number; unresolved_product_rows: number; resolved_customer_rows: number; unresolved_customer_rows: number };
  selected_period_quality?: { earliest_period: string | null; latest_period: string | null; populated_period_count: number; populated_periods: string[]; country_count: number; countries: string[]; model_count: number; source_batch_count: number; source_row_count: number; resolved_product_rows: number; unresolved_product_rows: number; resolved_customer_rows: number; unresolved_customer_rows: number; coverage_status: "COMPLETE" | "PARTIAL" | "MISSING"; signed_fact_rows: number; zero_fact_rows: number; source_batch_ids: string[] };
}

export interface AmazonChannelFlowMetricDTO {
  value: NeoNumeric;
  status: "AVAILABLE" | "MISSING";
}

export interface AmazonChannelFlowAlignmentDTO {
  value: NeoNumeric;
  status: "AVAILABLE" | "MISSING" | "BLOCKED" | "NOT_APPLICABLE";
  reason: string | null;
  closed_months: string[];
}

export interface AmazonChannelFlowCoverageDTO {
  source_rows: number;
  resolved_rows: number;
  unresolved_product_rows: number;
  unresolved_customer_rows: number;
  missing_value_rows: number;
  signed_fact_rows: number;
  zero_fact_rows: number;
  status: "COMPLETE" | "PARTIAL" | "MISSING";
}

export interface AmazonChannelFlowDriverDTO {
  key: string;
  period: string;
  country: string;
  master_model_id: string | null;
  model_number: string | null;
  product_identifiers: string[];
  po_intake_units: NeoNumeric;
  source_invoice_units?: NeoNumeric;
  reconstructed_units?: NeoNumeric;
  invoiced_sell_in_units: NeoNumeric;
  sell_out_units: NeoNumeric;
  net_channel_flow_units: NeoNumeric;
  open_po_units: NeoNumeric;
  coverage: AmazonChannelFlowCoverageDTO;
  signed_fact_rows: number;
  zero_fact_rows: number;
  evidence_count: number;
}

export interface AmazonChannelFlowDTO {
  view: "CHANNEL_FLOW";
  year: number;
  period_grain: Exclude<ChannelPeriodGrain, "WEEK">;
  selected_period: string;
  available_periods: string[];
  available_countries: string[];
  available_models: Array<{ master_model_id: string; model_number: string }>;
  selected_country: string | null;
  selected_model: string | null;
  as_of_date: string;
  period_status: "CURRENT_MTD" | "CLOSED";
  authorities: {
    po_intake: string;
    po_date: string;
    po_history_status: string;
    invoiced_sell_in: string;
    source_invoice_units: string;
    reconstructed_invoice_units: string;
    historical_reconstruction_rule: string;
    invoiced_amount: string;
    invoice_date: string;
    source_units_month_authority: "INVOICE_DATE";
    reconstructed_units_month_authority: "PO_ISSUE_MONTH";
    sell_out: string;
    sell_out_label: string;
    open_po: string;
    comparison: string;
  };
  outcomes: {
    po_intake_units: AmazonChannelFlowMetricDTO;
    source_invoice_units: AmazonChannelFlowMetricDTO;
    reconstructed_units: AmazonChannelFlowMetricDTO;
    known_sell_in_units: AmazonChannelFlowMetricDTO;
    invoiced_sell_in_units: AmazonChannelFlowMetricDTO;
    sell_in_coverage_status: "DETAILED" | "RECONSTRUCTED" | "MIXED" | "PARTIAL" | "MONTH_ALLOCATION_MISSING" | "MISSING";
    unsupported_document_count: number;
    month_allocation_missing_po_count: number;
    month_allocation_missing_units: AmazonChannelFlowMetricDTO;
    invoiced_amount_eur: AmazonChannelFlowMetricDTO;
    sell_out_units: AmazonChannelFlowMetricDTO;
    net_channel_flow_units: AmazonChannelFlowMetricDTO;
    open_po_units: AmazonChannelFlowMetricDTO;
    sell_through_alignment_3m: AmazonChannelFlowAlignmentDTO;
  };
  trend: Array<{
    period: string;
    period_status: "CURRENT_MTD" | "CLOSED" | "FUTURE";
    po_intake_units: NeoNumeric;
    source_invoice_units: NeoNumeric;
    reconstructed_units: NeoNumeric;
    known_sell_in_units: NeoNumeric;
    invoiced_sell_in_units: NeoNumeric;
    source_units_month_authority: "INVOICE_DATE";
    reconstructed_units_month_authority: "PO_ISSUE_MONTH";
    coverage_status: "DETAILED" | "RECONSTRUCTED" | "MIXED" | "PARTIAL" | "MONTH_ALLOCATION_MISSING" | "MISSING";
    unsupported_document_count: number;
    unsupported_po_count: number;
    reconstructed_po_count: number;
    month_allocation_missing_po_count: number;
    month_allocation_missing_units: NeoNumeric;
    invoiced_amount_eur: NeoNumeric;
    confirmed_invoice_amount: NeoNumeric;
    sell_out_units: NeoNumeric;
    net_channel_flow_units: NeoNumeric;
    alignment_3m: AmazonChannelFlowAlignmentDTO;
  }>;
  country_drivers: AmazonChannelFlowDriverDTO[];
  model_drivers: AmazonChannelFlowDriverDTO[];
  coverage: {
    po: AmazonChannelFlowCoverageDTO;
    invoice: AmazonChannelFlowCoverageDTO;
    sell_out: AmazonChannelFlowCoverageDTO;
    status: "COMPLETE" | "PARTIAL" | "MISSING";
  };
  exceptions: Array<{ code: string; count: number; status: "CONFIRMED" }>;
  evidence: { po: string; invoice: string; invoice_summary: string; sell_out: string; identity: string; open_po_evidence_count: number };
  historical_reconstruction: {
    rule_version: string;
    authority: string;
    reconstructed_units_month_authority: "PO_ISSUE_MONTH";
    scope: string;
    model_grain_source_only: boolean;
    summary_source_batch_ids: string[];
    evaluated_po_group_count: number;
    monthly_reconstructed_po_group_count: number;
    rejected_po_group_count: number;
    detailed_overlap_group_count: number;
    month_allocation_missing_po_count: number;
    month_allocation_missing_units: NeoNumeric;
    affected_reconstructed_months: Array<{ month: string; po_reconstructed_units: NeoNumeric; reconstructed_po_count: number; unsupported_po_count: number; reconstructed_units_month_authority: "PO_ISSUE_MONTH" }>;
  };
  semantic_rules: { po_intake_is_invoiced_sell_in: false; net_channel_flow_is_inventory: false; revenue_comparison_available: false; invoiced_amount_is_sell_out_revenue: false; week_grain_available: false; react_calculation_required: false; reconstructed_units_are_invoice_line_source_quantity: false; cross_month_po_units_allocated_proportionally: false; reconstructed_units_use_po_issue_month: true; invoice_amount_uses_invoice_date: true; de_normalization_scope: string };
}

export interface AmazonInventoryCommercialRowDTO {
  key: string;
  country: string;
  marketplace: string;
  master_model_id: string;
  model_number: string;
  asin: string;
  snapshot_date: string;
  marketplace_inventory: NeoNumeric;
  pan_eu_stock: NeoNumeric;
  pan_eu_marketplace_count: number | null;
  trailing_weeks_required: number;
  trailing_weeks_available: number;
  reporting_week_ends: string[];
  trailing_4w_sell_out_units: NeoNumeric;
  average_weekly_sell_out_units: NeoNumeric;
  woc: NeoNumeric;
  woc_status: "AVAILABLE" | "PARTIAL" | "MISSING" | "NO_SALES";
  sales_coverage: "COMPLETE" | "PARTIAL" | "MISSING";
  inventory_coverage: "AVAILABLE" | "MISSING";
  woc_missing_reason: string | null;
  source_sales_identity_weeks: number;
  sparse_zero_identity_weeks: number;
  unresolved_identity_weeks: number;
  weeks_required: number;
  weeks_available: number;
  risk_status: string;
  po_units: NeoNumeric;
  invoice_completion_units: NeoNumeric;
  open_po_units: NeoNumeric;
  in_transit_units: NeoNumeric;
  in_transit_resolution_status: "AVAILABLE" | "PARTIAL";
  in_transit_missing_count: number;
  po_status: string;
  coverage_status: "COMPLETE" | "PARTIAL";
  missing_states: string[];
  source_file: string | null;
  source_batch_id: string;
  source_lineage: Record<string, unknown>;
  po_evidence_count: number;
}

export interface JoybuySalesRollupDTO {
  units: NeoNumeric;
  revenue_eur: NeoNumeric;
  document_count: number;
  line_count: number;
  country_count: number;
  model_count: number;
  revenue_status: "COMPLETE" | "PARTIAL" | "MISSING";
  units_status: "COMPLETE" | "PARTIAL" | "MISSING";
}

export interface JoybuySalesDriverDTO extends JoybuySalesRollupDTO {
  key: string;
  country: string;
  master_model_id: string | null;
  model_number: string | null;
  revenue_covered_lines: number;
  revenue_missing_lines: number;
  product_resolved_lines: number;
  product_unresolved_lines: number;
  country_resolved_lines: number;
  country_unresolved_lines: number;
  free_of_charge_lines: number;
  signed_line_count: number;
}

export interface JoybuySalesLineEvidenceDTO {
  invoice_line_id: string;
  line_number: string | null;
  source_row: number | null;
  source_page: string | null;
  sku: string | null;
  country: string | null;
  master_model_id: string | null;
  model_number: string | null;
  quantity: NeoNumeric;
  raw_amount: NeoNumeric;
  raw_currency: string | null;
  reporting_revenue_eur: NeoNumeric;
  revenue_status: string;
  product_resolution: string;
  lineage: string[];
}

export interface JoybuySalesDocumentDTO extends JoybuySalesRollupDTO {
  invoice_id: string;
  invoice_number: string;
  invoice_date: string;
  customer: string;
  country: string | null;
  source_file: string | null;
  source_batch_id: string | null;
  source_sha256: string | null;
  free_of_charge_lines: number;
  revenue_missing_lines: number;
  lines: JoybuySalesLineEvidenceDTO[];
}

export interface JoybuySalesCommercialDTO {
  view: "JOYBUY_SALES";
  year: number;
  period_grain: PeriodGrain;
  selected_period: string;
  period_state: "CURRENT_PERIOD" | "HISTORICAL_PERIOD";
  available_periods: string[];
  available_countries: string[];
  available_models: Array<{ master_model_id: string; model_number: string }>;
  selected_country: string | null;
  selected_model: string | null;
  free_of_charge_only: boolean;
  unresolved_only: boolean;
  outcomes: JoybuySalesRollupDTO;
  trend: Array<JoybuySalesRollupDTO & { period: string }>;
  country_drivers: JoybuySalesDriverDTO[];
  model_drivers: JoybuySalesDriverDTO[];
  documents: JoybuySalesDocumentDTO[];
  coverage: {
    line_count: number;
    revenue_covered_lines: number;
    revenue_missing_lines: number;
    product_resolved_lines: number;
    product_unresolved_lines: number;
    country_resolved_lines: number;
    country_unresolved_lines: number;
    free_of_charge_lines: number;
    signed_line_count: number;
    active_documents_year: number;
    superseded_documents_year: number;
    active_lines_year: number;
    superseded_lines_year: number;
    replay_runs_year: number;
    latest_invoice_date: string | null;
    status: "COMPLETE" | "PARTIAL" | "MISSING";
  };
  exceptions: Array<{ code: string; count: number; status: "MISSING" | "UNRESOLVED"; action: string }>;
  authorities: {
    business_contract: "BUSINESS_CONTRACT_JOYBUY_COUNTRY_SELLIN_V1";
    units: string;
    revenue: string;
    period: string;
    identity: string;
    source: string;
    target_completion: "NOT_IMPLIED";
  };
  source_contributions: { official_pdf_invoice_lines: number; bulk_reconciliation_sales_actual: 0; joybuy_operation_sell_out: 0 };
  evidence: { invoice_uploads: string; identity: string; sell_out_traffic: string };
}

export interface JoybuySalesAlignmentMetricDTO {
  value: NeoNumeric;
  status: string;
  reason?: string | null;
}

export interface JoybuySalesAlignmentCoverageDTO {
  source_rows: number;
  resolved_rows: number;
  unresolved_product_rows: number;
  unresolved_country_rows: number;
  missing_value_rows: number;
  signed_fact_rows: number;
  zero_fact_rows: number;
  status: "COMPLETE" | "PARTIAL" | "MISSING";
}

export interface JoybuySalesAlignmentDriverDTO {
  key: string;
  period: string;
  country: string;
  master_model_id: string | null;
  model_number: string | null;
  invoiced_sell_in_units: NeoNumeric;
  sell_out_units: NeoNumeric;
  net_channel_flow_units: NeoNumeric;
  alignment_3m: JoybuySalesAlignmentMetricDTO & { closed_months: string[] };
  sell_in_mapping_status: string;
  sell_out_mapping_status: string;
  comparable_status: string;
  blocking_reason: string | null;
  coverage: { sell_in: JoybuySalesAlignmentCoverageDTO; sell_out: JoybuySalesAlignmentCoverageDTO };
  evidence: { sell_in: number; sell_out: number };
}

export interface JoybuySalesAlignmentDTO {
  view: "SALES_ALIGNMENT";
  year: number;
  period_grain: PeriodGrain;
  selected_period: string;
  available_periods: string[];
  selected_country: string | null;
  selected_model: string | null;
  available_countries: string[];
  available_models: Array<{ master_model_id: string; model_number: string }>;
  period_status: "CURRENT_MTD" | "CLOSED";
  as_of_date: string | null;
  authorities: Record<string, string>;
  outcomes: {
    invoiced_sell_in_units: JoybuySalesAlignmentMetricDTO;
    sell_out_units: JoybuySalesAlignmentMetricDTO;
    sell_out_actual_units: JoybuySalesAlignmentMetricDTO;
    sell_out_actual_revenue: JoybuySalesAlignmentMetricDTO;
    sell_out_source_status: string;
    comparable_sell_in_units: JoybuySalesAlignmentMetricDTO;
    comparable_sell_out_units: JoybuySalesAlignmentMetricDTO;
    comparison_status: string;
    net_channel_flow_units: JoybuySalesAlignmentMetricDTO;
    sell_through_alignment_3m: JoybuySalesAlignmentMetricDTO & { closed_months: string[] };
  };
  source_scope: { invoiced_sell_in_units: NeoNumeric; sell_out_units: NeoNumeric; meaning: "UNALIGNED_SOURCE_TOTALS_FOR_COVERAGE_EVIDENCE_ONLY" };
  trend: Array<{
    period: string;
    period_status: string;
    invoiced_sell_in_units: NeoNumeric;
    sell_out_units: NeoNumeric;
    sell_out_actual_units: NeoNumeric;
    sell_out_actual_revenue: NeoNumeric;
    sell_out_source_status: string;
    comparable_sell_in_units: NeoNumeric;
    comparable_sell_out_units: NeoNumeric;
    comparison_status: string;
    net_channel_flow_units: NeoNumeric;
    comparable_status: string;
    alignment_3m: JoybuySalesAlignmentMetricDTO & { closed_months: string[] };
  }>;
  country_drivers: JoybuySalesAlignmentDriverDTO[];
  model_drivers: JoybuySalesAlignmentDriverDTO[];
  comparable_scope: {
    sell_in_countries: string[];
    sell_out_countries: string[];
    comparable_countries: string[];
    non_comparable_countries: string[];
    comparable_model_count: number;
    comparable_pair_count: number;
    excluded_pair_count: number;
    unresolved_rows: number;
    status: "COMPLETE" | "PARTIAL" | "MISSING";
    blocking_reason: string | null;
  };
  coverage: { sell_in: JoybuySalesAlignmentCoverageDTO; sell_out: JoybuySalesAlignmentCoverageDTO; status: "COMPLETE" | "PARTIAL" | "MISSING"; alignment_status: string };
  exceptions: Array<{ code: string; count: number; status: "CONFIRMED" }>;
  evidence: { sell_in: string; sell_out: string; identity: string; operation: string; inventory: string };
  semantic_rules: { po_layer_available: false; revenue_comparison_available: false; net_channel_flow_is_inventory: false; week_grain_available: false; missing_is_zero: false; react_calculation_required: false };
}

export interface AmazonInventoryCommercialDTO {
  view: "AMAZON_INVENTORY";
  as_of_date: string | null;
  supply_as_of_date: string;
  selected_country: string | null;
  selected_model: string | null;
  selected_asin: string | null;
  available_countries: string[];
  available_models: Array<[string, string]>;
  available_asins: string[];
  available_snapshots: string[];
  authorities: Record<string, string>;
  outcomes: {
    marketplace_inventory: NeoNumeric;
    pan_eu_stock: NeoNumeric;
    trailing_weeks_required: number;
    trailing_weeks_available: number;
    reporting_week_ends: string[];
    trailing_4w_sell_out_units: NeoNumeric;
    average_weekly_sell_out_units: NeoNumeric;
    woc: NeoNumeric;
    woc_status: "AVAILABLE" | "PARTIAL" | "MISSING" | "NO_SALES";
    sales_coverage: "COMPLETE" | "PARTIAL" | "MISSING";
    inventory_coverage: "AVAILABLE" | "MISSING";
    woc_missing_reason: string | null;
  };
  supply_position: { po_units: NeoNumeric; invoice_completion_units: NeoNumeric; open_po_units: NeoNumeric; in_transit_units: NeoNumeric; status: "AVAILABLE" | "PARTIAL" | "MISSING"; evidence_count: number; missing_count: number; resolution_counts: Record<"CALCULATED" | "REVIEWED" | "MISSING", number> };
  rows: AmazonInventoryCommercialRowDTO[];
  trend: Array<{ snapshot_date: string; marketplace_inventory: NeoNumeric; pan_eu_stock: NeoNumeric; woc: NeoNumeric }>;
  trend_status: { marketplace_inventory: "AVAILABLE" | "PARTIAL" | "NOT_AVAILABLE"; pan_eu_stock: "AVAILABLE" | "PARTIAL" | "NOT_AVAILABLE"; woc: "AVAILABLE" | "PARTIAL" | "NOT_AVAILABLE" };
  coverage: { status: "COMPLETE" | "PARTIAL" | "MISSING"; row_count: number; inventory_covered_rows: number; pan_eu_covered_products: number; pan_eu_product_count: number; woc_covered_rows: number; po_covered_rows: number; source_sales_identity_weeks: number; sparse_zero_identity_weeks: number; unresolved_identity_weeks: number; weeks_required: number; weeks_available: number };
  exceptions: Array<{ code: string; count: number; status: "CONFIRMED" }>;
  evidence: { inventory: string; sell_out: string; po_invoice: string; identity: string; channel_flow: string; woc: { inventory_snapshot_date: string; inventory_row_count: number; selected_reporting_week_ends: string[]; sell_out_authority: string; source_sales_identity_weeks: number; sparse_zero_identity_weeks: number; unresolved_identity_weeks: number; weeks_required: number; weeks_available: number; source_value_semantic: "SOURCE"; sparse_zero_semantic: "SPARSE_WEEK_ZERO"; calculation_note: string } };
  semantic_rules: { react_calculation_required: false; pan_eu_filtered_by_country: false; in_transit_equals_open_po_authority: true; woc_history_available: false; woc_formula: string; reporting_weeks_split_or_prorated: false; future_week_end_excluded: true; aggregate_woc_averages_row_woc: false; sparse_week_zero_for_woc: true; sparse_week_zero_requires_complete_authoritative_week: true; sparse_week_zero_writes_sales_facts: false };
  read_only: true;
}

export interface AmazonCrossMarketCellDTO {
  on_hand: NeoNumeric;
  in_transit: NeoNumeric;
  stock_status: string;
  in_transit_status: "AVAILABLE" | "PARTIAL" | "NOT_APPLICABLE";
  in_transit_missing_count: number;
  stock_cutoff: string | null;
  in_transit_cutoff: string | null;
}

export interface AmazonCrossMarketStockMatrixRowDTO {
  master_model_id: string;
  model_number: string;
  pan_eu: AmazonCrossMarketCellDTO;
  countries: Record<"DE" | "FR" | "IT" | "ES", AmazonCrossMarketCellDTO>;
  status: "BALANCED" | "COUNTRY_STOCKOUT" | "HIGHLY_IMBALANCED" | "INBOUND_REBALANCING" | "PARTIAL_DATA" | "PAN_EU_UNRESOLVED";
  blockers: string[];
  flags: { stockout: boolean; has_in_transit: boolean; missing_stock: boolean; pan_eu_missing: boolean; imbalance: boolean };
  export: Record<string, NeoNumeric | string>;
}

export interface AmazonCrossMarketStockMatrixDTO {
  view: "AMAZON_CROSS_MARKET_STOCK_MATRIX";
  inventory_cutoff: string | null;
  in_transit_cutoff: string | null;
  available_snapshots: string[];
  columns: ["PAN_EU", "DE", "FR", "IT", "ES"];
  authorities: Record<string, string>;
  rows: AmazonCrossMarketStockMatrixRowDTO[];
  export_rows: Array<Record<string, NeoNumeric | string>>;
  coverage: { status: "COMPLETE" | "PARTIAL" | "MISSING"; model_count: number; pan_eu_missing_count: number; country_stock_missing_count: number; in_transit_missing_count: number };
  semantic_rules: { missing_is_zero: false; pan_eu_plus_country_allowed: false; multi_asin_aggregation_allowed: false; arrived_inbound_included: false; react_calculation_required: false; highly_imbalanced_rule: string; layout_status_precedence: string[]; default_order: string };
  read_only: true;
}

export interface AmazonInTransitDTO {
  view: "AMAZON_IN_TRANSIT";
  as_of_date: string;
  formula: string;
  total_in_transit_quantity: NeoNumeric;
  resolution: { in_transit_quantity: NeoNumeric; resolved_row_count: number; missing_row_count: number; status: "AVAILABLE" | "PARTIAL"; calculated_row_count: number; reviewed_row_count: number };
  by_marketplace: Array<{ country: string; in_transit_quantity: NeoNumeric; resolved_row_count: number; missing_row_count: number; status: "AVAILABLE" | "PARTIAL" }>;
  by_model: Array<{ master_model_id: string; model_number: string; in_transit_quantity: NeoNumeric; resolved_row_count: number; missing_row_count: number; status: "AVAILABLE" | "PARTIAL" }>;
  rows: Array<Record<string, unknown>>;
  authorities: Record<string, string>;
  performance: { total_seconds: number; po_invoice_audit_seconds: number; current_decision_query_seconds: number; resolution_seconds: number; row_count: number; current_decision_query_count: 1; full_decision_history_scan: false };
  read_only: true;
}

export interface AmazonInboundLifecycleAuditRowDTO {
  key: string;
  purchase_order: string;
  purchase_order_line: string | null;
  marketplace: string;
  country: string;
  master_model_id: string;
  model_number: string;
  asin: string;
  accepted_units: NeoNumeric;
  accepted_cost_amount: NeoNumeric;
  currency: string;
  accepted_date: string;
  age_days: number;
  age_bucket: "0-14" | "15-30" | "31-60" | "61-90" | ">90";
  source_lifecycle_status: null;
  source_requested_units: NeoNumeric;
  source_asn_units: NeoNumeric;
  source_received_units: NeoNumeric;
  source_cancelled_units: NeoNumeric;
  source_remaining_units: NeoNumeric;
  invoice_verified_units: NeoNumeric;
  invoice_signed_units: NeoNumeric;
  invoice_amount: NeoNumeric;
  amount_match_status: string;
  candidate_remaining_unverified_units: NeoNumeric;
  current_system_inbound_contribution: NeoNumeric;
  invoice_numbers: string[];
  invoice_dates: string[];
  match_status: string;
  review_status: "SOURCE_OPEN_UNREVIEWED";
  recommended_classification: string;
  human_decision: null;
  approved_inbound_units: null;
  source_version: number;
  source_batch_id: string;
  source_file: string;
  source_row: number;
  source_validation_status: string;
  source_resolution_state: string;
  source_approval_evidence_state: string;
  source_materialization_eligible: boolean;
  source_fingerprint: string;
  temporary_closure_applied: boolean;
  evidence: string[];
}

export interface AmazonInboundLifecycleAuditDTO {
  view: "AMAZON_INBOUND_LIFECYCLE_AUDIT";
  as_of_date: string;
  verdict: "REVIEW_REQUIRED_NO_GOVERNED_LIFECYCLE_AUTHORITY";
  audit_grain: "MARKETPLACE_PO_ASIN_MASTER_MODEL";
  audit_grain_quality: "DEGRADED_PO_LINE_MISSING" | "EXACT_PO_LINE_AVAILABLE";
  current_formula: {
    formula: string;
    uses_po_status: false;
    uses_source_remaining: false;
    uses_received_quantity: false;
    uses_cancelled_quantity: false;
    uses_eta: false;
    current_system_po_invoice_inbound_units: NeoNumeric;
    separate_cpfr_confirmed_inbound_units: NeoNumeric;
    authoritative_governed_inbound_units: null;
  };
  summary: {
    non_complete_population_status: string;
    current_po_count: number;
    current_line_count: number;
    missing_po_line_rows: number;
    candidate_po_units: NeoNumeric;
    source_remaining_candidate_units: NeoNumeric;
    source_remaining_candidate_pos: number;
    source_remaining_candidate_lines: number;
    invoice_verified_units: NeoNumeric;
    candidate_unverified_units: NeoNumeric;
    old_over_90_units: NeoNumeric;
    fully_invoice_matched_lines: number;
    partially_invoice_matched_lines: number;
    po_without_invoice_lines: number;
    invoice_without_po_lines: number;
    quantity_mismatch_lines: number;
    amount_mismatch_lines: number;
    old_over_90_lines: number;
    unresolved_model_lines: number;
    unresolved_marketplace_lines: number;
  };
  age_buckets: Array<{ bucket: string; line_count: number }>;
  country_breakdown: Array<{ country: string; po_count: number; line_count: number; candidate_units: NeoNumeric; current_formula_units: NeoNumeric }>;
  match_breakdown: Array<{ status: string; line_count: number }>;
  rows: AmazonInboundLifecycleAuditRowDTO[];
  invoice_without_po: Array<Record<string, unknown>>;
  confirmed_inbound_evidence: Array<Record<string, unknown>>;
  export_rows: Array<Record<string, string | number | null>>;
  decision_options: string[];
  golden_contract: Record<string, string | boolean>;
  semantic_rules: Record<string, boolean>;
  read_only: true;
}

export type AmazonInboundDecision = "KEEP_IN_TRANSIT" | "PARTIALLY_IN_TRANSIT" | "MARK_COMPLETE" | "CANCELLED" | "NOT_AMAZON_INBOUND" | "NEEDS_MORE_EVIDENCE";
export interface AmazonInboundGovernanceRowDTO extends AmazonInboundLifecycleAuditRowDTO {
  lifecycle_identity_hash: string;
  normalized_po_reference: string;
  invoice_evidence_fingerprint: string;
  evidence_fingerprint: string;
  queue_code: string;
  queue_label: string;
  queue_priority: number;
  queue_memberships: string[];
  batch_eligible: boolean;
  identity_conflict: string | null;
  current_decision: AmazonInboundDecisionRecordDTO | null;
  governed_outstanding_inbound_units: NeoNumeric;
  governance_status: "SOURCE_OPEN_UNREVIEWED" | "REVIEWED" | "EVIDENCE_CHANGED_REVIEW_REQUIRED";
  decision_history: AmazonInboundDecisionRecordDTO[];
}
export interface AmazonInboundDecisionRecordDTO {
  decision_id: string;
  decision_version: number;
  decision: AmazonInboundDecision;
  approved_outstanding_units: NeoNumeric;
  reviewer: string;
  reviewed_at: string;
  comment: string;
  evidence_fingerprint: string;
  prior_decision_id: string | null;
  status: "CONFIRMED";
  evidence_state?: "CURRENT" | "EVIDENCE_CHANGED_REVIEW_REQUIRED";
}
export interface AmazonInboundGovernanceDTO extends Omit<AmazonInboundLifecycleAuditDTO, "view" | "rows" | "read_only"> {
  view: "AMAZON_INBOUND_HUMAN_GOVERNANCE";
  rows: AmazonInboundGovernanceRowDTO[];
  first_decision_package: AmazonInboundGovernanceRowDTO[];
  review_queue_summary: Array<{ queue_code: string; queue_label: string; row_count: number; po_count: number; accepted_units: NeoNumeric; legacy_proxy_units: NeoNumeric; batch_eligible_rows: number }>;
  priority_queue_summary: Array<{ queue_code: string; queue_label: string; row_count: number; po_count: number; accepted_units: NeoNumeric; legacy_proxy_units: NeoNumeric; batch_eligible_rows: number }>;
  po_without_invoice_summary: Array<{ evidence_group: string; age_bucket: string; row_count: number; po_count: number; accepted_units: NeoNumeric; legacy_proxy_units: NeoNumeric }>;
  persistence: { available: boolean; state: string };
  coverage: { status: "UNREVIEWED" | "PARTIAL" | "GOVERNED"; reviewed_rows: number; required_rows: number; coverage_ratio: NeoNumeric; legacy_proxy_units: NeoNumeric; reviewed_legacy_proxy_units: NeoNumeric; unreviewed_proxy_equivalent_units: NeoNumeric; governed_reviewed_inbound_units: NeoNumeric; governed_total_complete: boolean };
  contract: Record<string, string | boolean>;
  decision_semantics: Record<AmazonInboundDecision, string>;
  read_only: true;
}
export interface AmazonInboundDecisionCommand {
  lifecycle_identity_hash: string;
  decision: AmazonInboundDecision;
  approved_outstanding_units: NeoNumeric;
  comment: string;
  po_source_version_id?: string;
}
export interface AmazonInboundDecisionPreviewDTO {
  state: "READY";
  message: string;
  preview_token: string;
  decision_count: number;
  commands: Array<AmazonInboundDecisionCommand & Record<string, unknown>>;
  impact: Array<{ lifecycle_identity_hash: string; purchase_order: string; country: string; model_number: string; old_legacy_proxy_inbound: NeoNumeric; new_governed_inbound: NeoNumeric; delta: NeoNumeric; decision: AmazonInboundDecision }>;
  country_impact: Array<{ country: string; old_legacy_proxy_inbound: NeoNumeric; new_governed_inbound: NeoNumeric; delta: NeoNumeric }>;
  model_impact: Array<{ model_number: string; old_legacy_proxy_inbound: NeoNumeric; new_governed_inbound: NeoNumeric; delta: NeoNumeric }>;
  cross_market_matrix_impact: "NOT_CUTOVER_PREVIEW_ONLY";
  cpfr_impact: "NOT_CUTOVER_PREVIEW_ONLY";
  order_planning_impact: "NOT_CUTOVER_PREVIEW_ONLY";
  read_only: true;
}

export interface JoybuyInventoryCommercialRowDTO {
  key: string;
  country: string | null;
  master_model_id: string | null;
  model_number: string | null;
  source_sku: string | null;
  snapshot_date: string | null;
  customer_on_hand: NeoNumeric;
  in_transit: NeoNumeric;
  woc: NeoNumeric;
  woc_status: string;
  mapping_status: "EXACT_MASTER_MODEL" | "UNRESOLVED";
  coverage_status: "COMPLETE" | "PARTIAL" | "MISSING";
  source_status: string;
  source_code: string | null;
  source_batch_id: string | null;
  source_row_number: number | null;
  exceptions: string[];
}

export interface JoybuyInventoryCommercialDTO {
  view: "JOYBUY_INVENTORY";
  selected_country: string | null;
  selected_model: string | null;
  selected_source_sku: string | null;
  selected_snapshot: string | null;
  unresolved_only: boolean;
  selected_woc_state: "AVAILABLE" | "MISSING" | null;
  available_countries: string[];
  available_models: Array<[string, string]>;
  available_source_skus: string[];
  available_snapshots: string[];
  authorities: Record<string, string>;
  outcomes: { customer_on_hand: NeoNumeric; in_transit: NeoNumeric; woc: NeoNumeric; woc_status: string; snapshot_date: string | null };
  position_reconciliation: { headline_in_transit: NeoNumeric; mapped_in_transit_units: NeoNumeric; unresolved_in_transit_units: NeoNumeric; mapped_on_hand_units: NeoNumeric; unresolved_on_hand_units: NeoNumeric; status: "RECONCILED" | "MISMATCH" };
  country_rows: Array<{ country: string; customer_on_hand: NeoNumeric; in_transit: NeoNumeric; woc: NeoNumeric; woc_status: string; snapshot_date: string | null; mapped_rows: number; unresolved_rows: number; unresolved_on_hand_units: NeoNumeric; unresolved_in_transit_units: NeoNumeric; source_status: string; evidence: string }>;
  rows: JoybuyInventoryCommercialRowDTO[];
  trend: Array<{ snapshot_date: string; customer_on_hand: NeoNumeric; in_transit: NeoNumeric; woc: NeoNumeric; woc_status: string; source_status: string }>;
  trend_status: { customer_on_hand: "AVAILABLE" | "PARTIAL" | "NOT_AVAILABLE"; in_transit: "AVAILABLE" | "PARTIAL" | "NOT_AVAILABLE"; woc: "AVAILABLE" | "PARTIAL" | "NOT_AVAILABLE" };
  coverage: { status: "COMPLETE" | "PARTIAL" | "MISSING"; row_count: number; on_hand_covered_rows: number; in_transit_covered_rows: number; woc_covered_rows: number; mapped_rows: number; unresolved_rows: number; mapping_coverage: number | null; mapped_on_hand_units: NeoNumeric; unresolved_on_hand_units: NeoNumeric; mapped_in_transit_units: NeoNumeric; unresolved_in_transit_units: NeoNumeric; country_count: number; model_count: number; active_inventory_fact_count: number; historical_generation_count: number; source_freshness_status: string; source_context_normalization_status: string };
  exceptions: Array<{ code: string; count: number; status: "CONFIRMED" }>;
  evidence: { inventory_source: string; mapping: string; snapshot: string; sales_alignment: string; operation: string };
  semantic_rules: { react_calculation_required: false; legacy_contexts_are_inventory_pools: false; historical_snapshots_are_summed: false; in_transit_derived_from_flow: false; missing_is_zero: false; fuzzy_mapping_allowed: false };
  read_only: true;
}

export interface InventoryAlertDTO {
  master_model_id: string;
  model_number: string;
  inventory: NeoNumeric;
  customer_on_hand: NeoNumeric;
  customer_in_transit: NeoNumeric;
  woc_or_dos: NeoNumeric;
  status: string;
  snapshot_dates: string[];
  reason: string;
}

export interface NeoPlanningInventoryDTO {
  as_of_date: string;
  region_scope: string;
  inventory: {
    matrix_total: Record<string, NeoNumeric>;
    dynamic_inventory: {
      status: string;
      authority: string;
      read_only: true;
      baseline: null | { snapshot_date: string; cutover_date: string; source_batch_id: string; source_file: string | null; source_checksum: string | null; source_fact_count: number; quantity_field: string };
      summary: null | { source_snapshot_available: NeoNumeric; snapshot_available: NeoNumeric; unresolved_snapshot_available: NeoNumeric; post_snapshot_rn_known: NeoNumeric; applied_post_snapshot_rn: NeoNumeric; blocked_missing_baseline_rn: NeoNumeric; unresolved_model_rn_known: NeoNumeric; net_dynamic_position: NeoNumeric; available_for_allocation: NeoNumeric; inventory_shortfall: NeoNumeric; summary_status: "COMPLETE" | "PARTIAL" | "MISSING"; model_count: number; movement_count: number; unresolved_model_row_count: number; missing_quantity_row_count: number };
      rows: Array<{ master_model_id: string; model_number: string | null; snapshot_available: NeoNumeric; post_snapshot_rn_known: NeoNumeric; open_rn_quantity: NeoNumeric; match_completed_rn_quantity: NeoNumeric; other_rn_quantity: NeoNumeric; unresolved_rn_quantity: NeoNumeric; net_dynamic_position: NeoNumeric; available_for_allocation: NeoNumeric; inventory_shortfall: NeoNumeric; coverage: "COMPLETE" | "PARTIAL" | "MISSING"; dynamic_available_status: string; status: string }>;
      movements: Array<{ movement_key: string; business_date: string; customer: string | null; source_customer: string | null; master_model_id: string | null; model_number: string | null; lifecycle_status: string; quantity: NeoNumeric; quantity_coverage: "COMPLETE" | "MISSING"; row_count: number }>;
      rn_depletion_audit: { authority: "RN_CURRENT_ACCEPTED_SOURCE_VERSION"; role: "POST_SNAPSHOT_INVENTORY_DEPLETION"; source_batch_ids: string[]; source_versions: string[]; post_snapshot_row_count: number; quantity_row_count: number; resolved_model_row_count: number; model_unresolved_row_count: number; quantity_missing_row_count: number; post_snapshot_rn_known: NeoNumeric; unresolved_model_rn_known: NeoNumeric; deduction_applied: true; lifecycle_role: "DIAGNOSTIC_ONLY"; coverage: "COMPLETE" | "PARTIAL" | "MISSING" };
      exceptions: Array<{ code: string; count: number }>;
    };
    customer_in_transit_reconciliation: {
      amazon_po_quantity: NeoNumeric;
      amazon_po_key_count: number;
      amazon_open_po_quantity: NeoNumeric;
      amazon_invoice_completed_quantity: NeoNumeric;
      joybuy_weekly_in_transit_quantity: NeoNumeric;
      exact_po_match_count: number;
      amazon_country_rows: Array<{
        country: string;
        po_quantity: NeoNumeric;
        invoice_completed_quantity: NeoNumeric;
        open_quantity: NeoNumeric;
      }>;
      amazon_model_rows: Array<{
        master_model_id: string;
        model_number: string;
        po_quantity: NeoNumeric;
        invoice_completed_quantity: NeoNumeric;
        open_quantity: NeoNumeric;
      }>;
      amazon_po_invoice_evidence: Array<{
        purchase_order: string;
        country: string;
        customer_code: string;
        master_model_id: string;
        model_number: string;
        source_product_identifiers: string[];
        po_quantity: NeoNumeric;
        invoice_completed_quantity: NeoNumeric;
        remaining_quantity: NeoNumeric;
        invoice_numbers: string[];
        invoice_line_ids: string[];
        source_fact_ids: string[];
        reconciliation_status: string;
        relationship_status: string;
      }>;
    };
    product_supply_chain_on_hand_quantity: NeoNumeric;
    unresolved_supply_chain_on_hand_contribution: NeoNumeric;
    product_reconciliation_status: string;
    product_reconciliation_buckets: Array<{
      source_family: string;
      classification: string;
      fact_count: number;
      quantity: NeoNumeric;
      stock_contribution_quantity: NeoNumeric;
      source_identities: string[];
      reason: string;
    }>;
    product_rows: Array<{
      master_model_id: string;
      canonical_product: string;
      annual_hq_ordered: NeoNumeric;
      supply_chain_on_hand: NeoNumeric;
      supply_chain_on_hand_status: string;
      supply_chain_on_hand_warning: string | null;
      customer_on_hand: NeoNumeric;
      customer_in_transit: NeoNumeric;
      known_end_to_end_inventory: NeoNumeric;
      complete_end_to_end_inventory: NeoNumeric;
      known_inventory_dos: NeoNumeric;
      customer_on_hand_snapshot_dates: string[];
      risk_status: string;
      data_completeness: string;
    }>;
  };
  scm: {
    summary: Record<string, NeoNumeric>;
    rows: Array<{
      canonical_model: string;
      customer_model: string | null;
      customer: string | null;
      country: string | null;
      swe_ordered_quantity: NeoNumeric;
      source_total_order_quantity: NeoNumeric;
      production_order_quantity: NeoNumeric;
      actual_produced_quantity: NeoNumeric;
      estimated_produced_quantity: NeoNumeric;
      effective_produced_quantity: NeoNumeric;
      production_status: string | null;
      local_inventory_quantity: NeoNumeric;
      ttl_scm_inventory_quantity: NeoNumeric;
      warnings: string[];
    }>;
    blocked_dq_count: number;
    unmatched_production_count: number;
    poland_snapshot_date: string | null;
    psi_snapshot_date: string | null;
  };
  scm_production: {
    scope: "SWE_STRUCTURED_ALLOCATION_ONLY";
    rows: Array<{
      model_number: string | null; jo: string; production_order: string | null;
      swe_allocated: NeoNumeric; produced: NeoNumeric; remaining: NeoNumeric;
      production_status: string; actual_production_date: null;
      planned_production_date: string | null; stock_age_days: null;
      days_to_production: number | null; overdue: boolean; source_as_of: string | null;
      source_lineage: Record<string, unknown>;
    }>;
    summary: { swe_allocated: NeoNumeric; produced: NeoNumeric; remaining: NeoNumeric; produced_rows: number; pending_rows: number };
    stock_age_authority: string;
    stock_age_status: "MISSING";
    planned_date_authority: string;
  };
  forecast: {
    base: {
      import_result: { forecast_version: string; approval_status: string; total_forecast_quantity: NeoNumeric; submitted_row_count: number; fact_count: number };
      invoice_sales_coverage: { invoice_sales_total_quantity: NeoNumeric; forecast_scope_included_quantity: NeoNumeric; forecast_scope_excluded_quantity: NeoNumeric; unresolved_product_quantity: NeoNumeric } | null;
    };
    matched_committed_order_quantity: NeoNumeric;
    achievement_percent: NeoNumeric;
    product_mapping_required: Array<Record<string, unknown>>;
    group_risk_summary: Array<{ reporting_customer_group: string; future_forecast_quantity: NeoNumeric; assigned_supply_quantity: NeoNumeric; shortage_quantity: NeoNumeric; excess_quantity: NeoNumeric; risk_status: string }>;
    [key: string]: unknown;
  };
  annual_ordered_quantity: { authority: string; model_identity: string; total_quantity: NeoNumeric; resolved_model_quantity: NeoNumeric; unresolved_model_quantity: NeoNumeric; resolved_fact_count: number; unresolved_fact_count: number; reconciliation_status: string };
  forecast_coverage: { source_customer_count: number; resolved_customer_count: number; unresolved_customer_count: number; unresolved_customer_units: NeoNumeric; source_model_count: number; resolved_model_count: number; unresolved_model_count: number; unresolved_model_units: NeoNumeric; status: string };
  alerts: Record<"HIGH_TURNOVER" | "LOW_TURNOVER" | "LOW_INVENTORY" | "HIGH_INVENTORY", InventoryAlertDTO[]>;
  alert_policy: string;
  customer_stock_date_semantics: string;
  rn_sales_contribution: 0;
  rn_positive_in_transit_contribution: 0;
}

export interface NeoPlanningInventoryBootstrapDTO {
  as_of_date: string;
  region_scope: string;
  response_mode: "SUMMARY_FIRST";
  dynamic_inventory: Omit<NeoPlanningInventoryDTO["inventory"]["dynamic_inventory"], "movements" | "baseline" | "rn_depletion_audit"> & {
    baseline: null | Omit<NonNullable<NeoPlanningInventoryDTO["inventory"]["dynamic_inventory"]["baseline"]>, "source_batch_id" | "source_checksum">;
    rn_depletion_audit: Omit<NeoPlanningInventoryDTO["inventory"]["dynamic_inventory"]["rn_depletion_audit"], "source_batch_ids">;
  };
  omitted_initial_detail: string[];
}

export interface NeoPaymentAuthorityDTO {
  status: "AVAILABLE" | "PAYMENT_SETTLEMENT_AUTHORITY_MISSING";
  source_batch_count: number;
  evidence_row_count: number;
  reconciliation_run_count: number;
  internal_review_is_payment_evidence: false;
  missing_external_source_contract: string | null;
}

export interface NeoDataAuthorityDTO {
  year: number;
  as_of_date: string;
  customer_identity: {
    business_year: number;
    decision: "CANONICAL_CUSTOMER_WITH_MULTI_SOURCE_IDENTITIES";
    raw_source_identity_count: number;
    canonical_customer_count: number;
    total_sales_quantity: NeoNumeric;
    raw_invoice_evidence_immutable: true;
    source_rows: Array<Record<string, unknown>>;
    canonical_rows: Array<Record<string, unknown>>;
    mappings: Array<Record<string, unknown>>;
  };
  forecast: {
    source_customer_count: number; resolved_customer_count: number; unresolved_customer_count: number;
    resolved_customer_units: NeoNumeric; unresolved_customer_units: NeoNumeric;
    source_model_count: number; resolved_model_count: number; unresolved_model_count: number;
    resolved_model_units: NeoNumeric; unresolved_model_units: NeoNumeric;
    unmatched_customers: string[]; unmatched_models: string[];
    customer_coverage: Array<{ source_customer: string; country: string | null; resolved_customer_ids: string[]; model_count: number; forecast_units: NeoNumeric; root_causes: string[]; status: string }>;
  };
  cost: {
    latest_available_month: string | null; total_source_rows: number; source_rows_by_type: Record<string, number>;
    business_row_count: number; resolved_business_models: number; models_with_exw: number; models_with_inventory_cost: number;
    unresolved_rows: number; ambiguous_rows: number; conflict_rows: number; exact_source_model_mapping_count: number;
    review_rows: Array<{ review_key: string; source_identifier: string | null; source_model: string | null; source_type: string; business_month: string; currency: string | null; resolution_status: string; issue_code: string; source_batch: string | null; source_file: string | null; source_sheet: string | null; source_row: number | null }>;
  };
  customer_kpi: {
    authority: string; source_type: string; business_grain: string; target_year: number;
    period_coverage: string[]; customer_coverage: number; target_row_count: number;
    target_units_status: string; target_revenue_status: string; target_revenue_customer_coverage: number;
    target_units_month_coverage: string[]; target_revenue_month_coverage: string[];
    target_units_cell_coverage: number; target_revenue_cell_coverage: number; target_expected_cell_coverage: number;
    missing_target_revenue_customers: string[];
    currencies: string[]; source_lineage: string[]; latest_accepted_upload: string | null;
    latest_source_file: string | null; latest_source_sha: string | null; latest_import_batch_id: string | null;
    status: string; update_route: string; customer_identity_mutation: "FORBIDDEN"; frontend_formulas: "NONE";
  };
  payment: NeoPaymentAuthorityDTO;
  production_identity_blocker: string;
  business_authority_changes: "NONE";
}

export interface NeoPromotionProposalRowDTO {
  proposal_id: string;
  marketplace: string;
  country: string | null;
  asin: string;
  master_model_id: string;
  model_number: string | null;
  proposal_price: NeoNumeric;
  proposal_support_per_unit: NeoNumeric;
  forecast_units: NeoNumeric;
  true_net_revenue: NeoNumeric;
  product_cost: NeoNumeric;
  contribution_margin: NeoNumeric;
  contribution_margin_rate: NeoNumeric;
  calculation_state: string;
  current_supply_chain_on_hand: NeoNumeric;
  customer_on_hand: NeoNumeric;
  customer_in_transit: NeoNumeric;
  known_inventory_dos: NeoNumeric;
  inventory_risk_status: string;
  customer_on_hand_snapshot_dates: string[];
}
export interface NeoPromotionProposalDTO {
  status: string;
  proposal_run_id?: string;
  proposal_id?: string;
  created_at?: string;
  formula_version?: string;
  rows: NeoPromotionProposalRowDTO[];
  coverage_rule?: string;
  inventory_semantics?: string;
}

export interface JoybuyInventoryContextDTO {
  customer_on_hand: NeoNumeric;
  customer_in_transit: NeoNumeric;
  global_supply_chain_on_hand: NeoNumeric;
  global_available: NeoNumeric;
  global_available_authority: string;
  known_inventory_dos: NeoNumeric;
  recent_sell_out_30d: NeoNumeric;
  turnover_woc_30d: NeoNumeric;
  turnover_status: string;
  latest_inventory_cycle: string | null;
  status: string;
  supply_constraint_status: string;
  risk_reason: string;
  customer_on_hand_snapshot_dates: string[];
}
export interface NeoJoybuyPromotionDTO extends Omit<JoybuyPromotionDTO, "rows"> {
  rows: Array<JoybuyPromotionRowDTO & { inventory_context: JoybuyInventoryContextDTO }>;
  inventory_grain: "MASTER_MODEL";
  woc_aggregation: "NO_SKU_AVERAGE";
  business_purpose: "JOYBUY_ORDER_PLANNING";
  post_order_projection: { status: "NOT_CALCULATED_AUTHORITY_INSUFFICIENT"; reason: string; components_exposed_separately: true };
}

export interface NeoInventoryAnalyticsRowDTO {
  country: string;
  master_model_id: string;
  model_number: string;
  customer_on_hand: NeoNumeric;
  customer_in_transit: NeoNumeric;
  recent_sell_out: NeoNumeric;
  sales_velocity_basis: string;
  woc_or_dos: NeoNumeric;
  turnover_status: string;
  risk_status: string;
  risk_reason: string;
  snapshot_date: string | null;
}

export interface NeoInventoryAnalyticsDTO {
  channel: "AMAZON" | "JOYBUY";
  as_of_date: string | null;
  authority: string;
  inventory_semantics: string;
  model_identity: "MASTER_MODEL_ID";
  woc_aggregation: string;
  risk_policy: string;
  country_rows: Array<{ country: string; customer_on_hand: NeoNumeric; customer_in_transit: NeoNumeric; recent_sell_out: NeoNumeric; woc_or_dos: NeoNumeric; turnover_status: string; model_count: number; risk_count: number }>;
  model_rows: NeoInventoryAnalyticsRowDTO[];
  risk_matrix: NeoInventoryAnalyticsRowDTO[];
  trend: Array<{ period: string; country: string; customer_on_hand: NeoNumeric; customer_in_transit: NeoNumeric; recent_sell_out: NeoNumeric; status: string }>;
  coverage: { country_count: number; model_count: number; row_count: number; unresolved_model_rows: number; woc_available_rows: number; inventory_available_rows: number; status: string };
  business_question: string;
}

export interface JoybuyOperationDTO {
  source: { authority: string; weekly_batch_id: string | null; mapping_batch_id: string | null; historical_batch_id: string | null; historical_source_status: string; data_sources_route: string };
  selected_period: string | null;
  available_periods: string[];
  filters: { country: string | null; model: string | null; available_countries: string[] };
  summary: { uv_7: NeoNumeric; sales_qty_7: NeoNumeric; conversion_rate: null; conversion_status: string; on_hand_qty: NeoNumeric; in_transit_qty: NeoNumeric; woc_30d: NeoNumeric; woc_30d_status: string; attention_count: number };
  country_rows: Array<{ country: string; uv_7: NeoNumeric; sales_qty_7: NeoNumeric; on_hand_qty: NeoNumeric; in_transit_qty: NeoNumeric; woc_30d: NeoNumeric; woc_30d_status: string; sku_count: number; mapped_sku_count: number; unmapped_sku_count: number }>;
  model_rows: Array<{ country: string; master_model_id: string | null; model_number: string; mapping_status: string; sku_count: number; uv_7: NeoNumeric; sales_qty_7: NeoNumeric; sales_qty_30: NeoNumeric; conversion_rate_7: NeoNumeric; conversion_status: string; on_hand_qty: NeoNumeric; in_transit_qty: NeoNumeric; woc_30d: NeoNumeric; woc_30d_status: string; source_rows: Array<{ batch_id: string; row_number: number }> }>;
  traffic_sellout_trend: Array<{ period_end_date: string; site: string; uv_7: NeoNumeric; sales_qty_7: NeoNumeric; on_hand_inventory: NeoNumeric; in_transit_inventory: NeoNumeric }>;
  inventory_trend: Array<{ period_end_date: string; on_hand_qty: NeoNumeric; in_transit_qty: NeoNumeric }>;
  conversion_trend: Array<{ period_end_date: string; country: string; conversion_rate: NeoNumeric; status: string; source_row_count: number; paired_row_count: number }>;
  model_watchlist: Array<{ country: string; master_model_id: string | null; model_number: string; mapping_status: string; sales_qty_7: NeoNumeric; on_hand_qty: NeoNumeric; woc_30d: NeoNumeric; attention_count: number; attention_states: string[]; ranking_authority: string }>;
  alerts: Array<{ code: string; reason: string; country: string | null; sku: string | null; model_number: string; period_end_date: string; source_batch_id: string; source_row_number: number }>;
  coverage: { available_periods: string[]; missing_periods: string[]; period_count: number; weekly_raw_rows: number; weekly_transformed_rows: number; weekly_matched_rows: number; weekly_unmapped_rows: number; weekly_conflict_rows: number; historical_raw_rows: number; missing_period_reason: string; conversion_reason: string; mapping_impact: Record<string, Record<string, NeoNumeric>> };
  lineage: { raw_to_transformed: string; transformed_to_resolved: string; resolved_to_dto: string; frontend_formulas: "NONE"; ai_usage: "NONE" };
}

export interface JoybuyOperationCommercialDTO {
  view: "JOYBUY_OPERATION";
  selected_period: string | null;
  available_periods: string[];
  filters: {
    country: string | null; model: string | null; conversion_state: string | null; sell_out_state: string | null; unresolved_only: boolean;
    available_countries: string[];
    available_models: Array<{ value: string; label: string; master_model_id: string | null; mapping_status: string }>;
    conversion_states: string[]; sell_out_states: string[];
  };
  authorities: { traffic: string; sell_out: string; conversion: string; grain: string; period: string; sales_alignment: string };
  business_context: { metric_scope: string; traffic_metric: string; traffic_unit: string; sell_out_unit: string; conversion_unit: string; conversion_aggregation: string };
  outcomes: { traffic_uv_7: NeoNumeric; traffic_status: string; sell_out_units: NeoNumeric; sell_out_status: string; conversion_rate: NeoNumeric; conversion_status: string };
  traffic_sellout_trend: Array<{ period_end_date: string; country: string; traffic_uv_7: NeoNumeric; sell_out_units: NeoNumeric }>;
  conversion_trend: Array<{ period_end_date: string; country: string; conversion_rate: NeoNumeric; status: string; source_row_count: number }>;
  country_rows: Array<{ country: string; traffic_uv_7: NeoNumeric; sell_out_units: NeoNumeric; conversion_rate: NeoNumeric; conversion_status: string; source_sku_count: number; unresolved_count: number }>;
  driver_rows: JoybuyOperationCommercialRowDTO[];
  relationships: Array<{ metric_code: string; label: string; current_value: NeoNumeric; previous_value: NeoNumeric; absolute_change: NeoNumeric; percentage_change: NeoNumeric; status: string }>;
  coverage: { source_row_count: number; country_count: number; model_count: number; traffic_covered_rows: number; source_traffic_rows: number; derived_traffic_rows: number; traffic_coverage: NeoNumeric; conversion_covered_rows: number; conversion_coverage: NeoNumeric; sell_out_covered_rows: number; sell_out_coverage: NeoNumeric; mapped_rows: number; unresolved_rows: number; mapping_coverage: NeoNumeric; selected_period: string | null; available_period_count: number; source_freshness_status: string; status: "COMPLETE" | "PARTIAL" | "MISSING" };
  exceptions: Array<{ code: string; reason: string; affected_rows: number; severity: string; sample_keys: string[] }>;
  evidence: { source_authority: string; source_route: string; mapping_authority: string; frontend_formulas: "NONE"; ai_usage: "NONE" };
  cross_links: Array<{ label: string; route: string; purpose: string }>;
}

export interface JoybuyOperationCommercialRowDTO {
  period_end_date: string | null; country: string; master_model_id: string | null; model_number: string; source_sku: string; mapping_status: string;
  traffic_uv_7: NeoNumeric; source_traffic_uv_7: NeoNumeric; traffic_status: string; conversion_rate_7: NeoNumeric; conversion_status: string; source_conversion_rate_7: NeoNumeric; sell_out_units: NeoNumeric; sell_out_status: string;
  signed_sell_out: boolean; source_code: string | null; source_row_number: number | null;
}

export interface V40GovernanceDTO {
  cost_source_family_semantics: string;
  families: V40CostSourceFamilyDTO[];
  bindings: Array<{ binding_id: string; family_code: string; source_context: string; source_model: string; factory: string | null; business_month: string | null; currency: string | null; source_file: string | null; source_version: string | null; is_active: boolean; actor: string; mapping_status: string }>;
  ad_group_mappings: Array<Record<string, unknown>>;
  unmapped_ad_groups: Array<{ source_type: string; marketplace: string; campaign: string; ad_group: string; date_from: string | null; date_to: string | null; spend: NeoNumeric; clicks: NeoNumeric; orders: NeoNumeric; affected_rows: number; mapping_status: string }>;
  audits: Array<Record<string, unknown>>;
  matching_rule: string;
}

export interface V40CostSourceFamilyDTO {
  cost_source_family_id: string;
  family_code: string;
  family_name: string;
  source_context: "MONTHLY_FACTORY_COST_QUOTATION";
  description: string | null;
  valid_from: string | null;
  valid_to: string | null;
  is_active: boolean;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface V40CreateCostSourceFamilyRequest {
  family_code: string;
  family_name: string;
  source_context?: "MONTHLY_FACTORY_COST_QUOTATION";
  description?: string | null;
  valid_from?: string | null;
  valid_to?: string | null;
  is_active?: boolean;
}

export interface V40BindCostSourceFamilyRequest {
  cost_source_family_id: string;
  source_model: string;
  source_context?: "MONTHLY_FACTORY_COST_QUOTATION";
  factory?: string | null;
  business_month?: string | null;
  currency?: string | null;
  source_file?: string | null;
  source_version?: string | null;
}

const periodQuery = (year: number, grain: ChannelPeriodGrain, period?: string) => {
  const query = new URLSearchParams({ year: String(year), period_grain: grain });
  if (period) query.set("period", period);
  return query.toString();
};

export const getNeoEnvironment = (signal?: AbortSignal) => apiClient.get<NeoEnvironmentDTO>("/api/neo/environment", { signal, timeoutMs: 15_000 });
export const getV40Governance = (signal?: AbortSignal) => apiClient.get<V40GovernanceDTO>("/api/neo/v40/governance", { signal, timeoutMs: 30_000 });
const governedV40Request = <T>(path: string, payload: object, actor: string) => apiClient.request<T>(path, { method: "POST", headers: { "Content-Type": "application/json", "x-mbip-actor": actor }, body: JSON.stringify(payload) });
export const createV40CostFamily = (payload: V40CreateCostSourceFamilyRequest, actor: string) => governedV40Request<V40CostSourceFamilyDTO>("/api/neo/v40/cost-source-families", payload, actor);
export const bindV40CostFamily = (payload: V40BindCostSourceFamilyRequest, actor: string) => governedV40Request<Record<string, unknown>>("/api/neo/v40/cost-source-family-bindings", payload, actor);
export const mapV40AdGroup = (payload: Record<string, unknown>, actor: string) => governedV40Request<Record<string, unknown>>("/api/neo/v40/ad-group-mappings", payload, actor);
export const setV40CostFamilyActive = (familyId: string, active: boolean, actor: string) => apiClient.request<Record<string, unknown>>(`/api/neo/v40/cost-source-families/${encodeURIComponent(familyId)}`, { method: "PATCH", headers: { "Content-Type": "application/json", "x-mbip-actor": actor }, body: JSON.stringify({ is_active: active }) });
export const getNeoProfitability = (year: number, grain: PeriodGrain, period?: string, signal?: AbortSignal, includeEvidence = false) => apiClient.get<NeoProfitabilityDTO>(`/api/neo/profitability?${periodQuery(year, grain, period)}${includeEvidence ? "&include_evidence=true" : ""}`, { signal, timeoutMs: 30_000 });
export const getNeoChannelSales = (channel: "AMAZON" | "JOYBUY", year: number, grain: ChannelPeriodGrain, period?: string, signal?: AbortSignal, filters: { country?: string; model?: string } = {}) => {
  const query = new URLSearchParams(periodQuery(year, grain, period));
  if (filters.country) query.set("country", filters.country);
  if (filters.model) query.set("model", filters.model);
  return apiClient.get<NeoChannelSalesDTO>(`/api/neo/sales/channel?channel=${channel}&${query}`, { signal, timeoutMs: 30_000 });
};
export const getAmazonChannelFlow = (year: number, grain: Exclude<ChannelPeriodGrain, "WEEK">, period?: string, signal?: AbortSignal, filters: { country?: string; model?: string } = {}) => {
  const query = new URLSearchParams(periodQuery(year, grain, period));
  if (filters.country) query.set("country", filters.country);
  if (filters.model) query.set("model", filters.model);
  return apiClient.get<AmazonChannelFlowDTO>(`/api/neo/sales/amazon-channel-flow?${query}`, { signal, timeoutMs: 30_000 });
};
export const getJoybuySalesCommercial = (
  year: number,
  grain: PeriodGrain,
  period?: string,
  signal?: AbortSignal,
  filters: { country?: string; model?: string; freeOfChargeOnly?: boolean; unresolvedOnly?: boolean } = {},
) => {
  const query = new URLSearchParams(periodQuery(year, grain, period));
  if (filters.country) query.set("country", filters.country);
  if (filters.model) query.set("model", filters.model);
  if (filters.freeOfChargeOnly) query.set("free_of_charge_only", "true");
  if (filters.unresolvedOnly) query.set("unresolved_only", "true");
  return apiClient.get<JoybuySalesCommercialDTO>(`/api/neo/sales/joybuy-invoiced?${query}`, { signal, timeoutMs: 30_000 });
};
export const getJoybuySalesAlignment = (
  year: number,
  grain: PeriodGrain,
  period?: string,
  signal?: AbortSignal,
  filters: { country?: string; model?: string } = {},
) => {
  const query = new URLSearchParams(periodQuery(year, grain, period));
  if (filters.country) query.set("country", filters.country);
  if (filters.model) query.set("model", filters.model);
  return apiClient.get<JoybuySalesAlignmentDTO>(`/api/neo/sales/joybuy-alignment?${query}`, { signal, timeoutMs: 30_000 });
};
export const getNeoPlanningInventoryBootstrap = (asOfDate: string, signal?: AbortSignal) => apiClient.get<NeoPlanningInventoryBootstrapDTO>(`/api/neo/planning-inventory/bootstrap?as_of_date=${encodeURIComponent(asOfDate)}&region_scope=SWE`, { signal });
export const getNeoPlanningInventory = (asOfDate: string, signal?: AbortSignal) => apiClient.get<NeoPlanningInventoryDTO>(`/api/neo/planning-inventory?as_of_date=${encodeURIComponent(asOfDate)}&region_scope=SWE`, { signal });
export const getNeoInventoryAnalytics = (channel: "AMAZON" | "JOYBUY", signal?: AbortSignal) => apiClient.get<NeoInventoryAnalyticsDTO>(`/api/neo/inventory/channel?channel=${channel}`, { signal, timeoutMs: 60_000 });

export const getAmazonInventoryCommercial = (
  filters: { country?: string; model?: string; asin?: string; snapshotDate?: string },
  signal?: AbortSignal,
) => {
  const query = new URLSearchParams();
  if (filters.country) query.set("country", filters.country);
  if (filters.model) query.set("model", filters.model);
  if (filters.asin) query.set("asin", filters.asin);
  if (filters.snapshotDate) query.set("snapshot_date", filters.snapshotDate);
  return apiClient.get<AmazonInventoryCommercialDTO>(
    `/api/neo/inventory/amazon-commercial?${query.toString()}`,
    { signal, timeoutMs: 60_000 },
  );
};
export const getAmazonCrossMarketStockMatrix = (
  snapshotDate?: string,
  signal?: AbortSignal,
) => {
  const query = new URLSearchParams();
  if (snapshotDate) query.set("snapshot_date", snapshotDate);
  return apiClient.get<AmazonCrossMarketStockMatrixDTO>(
    `/api/neo/inventory/amazon-cross-market-matrix${query.size ? `?${query}` : ""}`,
    { signal, timeoutMs: 60_000 },
  );
};
export const getAmazonInboundLifecycleAudit = (signal?: AbortSignal) =>
  apiClient.get<AmazonInboundLifecycleAuditDTO>(
    "/api/neo/inventory/amazon-inbound-lifecycle-audit",
    { signal, timeoutMs: 60_000 },
  );
export const getAmazonInTransit = (signal?: AbortSignal) =>
  apiClient.get<AmazonInTransitDTO>(
    "/api/neo/inventory/amazon-in-transit",
    { signal, timeoutMs: 60_000 },
  );
export const getAmazonInboundGovernance = (signal?: AbortSignal) =>
  apiClient.get<AmazonInboundGovernanceDTO>(
    "/api/neo/inventory/amazon-inbound-governance",
    { signal, timeoutMs: 60_000 },
  );
export const previewAmazonInboundDecisions = (decisions: AmazonInboundDecisionCommand[]) =>
  apiClient.request<AmazonInboundDecisionPreviewDTO>(
    "/api/neo/inventory/amazon-inbound-governance/preview",
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ decisions }), timeoutMs: 60_000 },
  );
export const confirmAmazonInboundDecisions = (preview: AmazonInboundDecisionPreviewDTO, actor: string) =>
  apiClient.request<{ state: "CONFIRMED"; decision_count: number }>(
    "/api/neo/inventory/amazon-inbound-governance/confirm",
    { method: "POST", headers: { "Content-Type": "application/json", "x-mbip-actor": actor }, body: JSON.stringify({ preview_token: preview.preview_token, decisions: preview.commands }), timeoutMs: 60_000 },
  );
export const getJoybuyInventoryCommercial = (
  filters: { country?: string; model?: string; sourceSku?: string; snapshotDate?: string; unresolvedOnly?: boolean; wocState?: "AVAILABLE" | "MISSING" },
  signal?: AbortSignal,
) => {
  const query = new URLSearchParams();
  if (filters.country) query.set("country", filters.country);
  if (filters.model) query.set("model", filters.model);
  if (filters.sourceSku) query.set("source_sku", filters.sourceSku);
  if (filters.snapshotDate) query.set("snapshot_date", filters.snapshotDate);
  if (filters.unresolvedOnly) query.set("unresolved_only", "true");
  if (filters.wocState) query.set("woc_state", filters.wocState);
  return apiClient.get<JoybuyInventoryCommercialDTO>(
    `/api/neo/inventory/joybuy-commercial${query.size ? `?${query}` : ""}`,
    { signal, timeoutMs: 60_000 },
  );
};
export const getJoybuyOperation = (filters: { period_end?: string; country?: string; model?: string } = {}, signal?: AbortSignal) => {
  const query = new URLSearchParams();
  if (filters.period_end) query.set("period_end", filters.period_end);
  if (filters.country) query.set("country", filters.country);
  if (filters.model) query.set("model", filters.model);
  return apiClient.get<JoybuyOperationDTO>(`/api/neo/joybuy-operation${query.size ? `?${query}` : ""}`, { signal, timeoutMs: 15_000 });
};
export const getJoybuyOperationCommercial = (filters: { periodEnd?: string; country?: string; model?: string; conversionState?: "AVAILABLE" | "MISSING"; sellOutState?: "ZERO"; unresolvedOnly?: boolean } = {}, signal?: AbortSignal) => {
  const query = new URLSearchParams();
  if (filters.periodEnd) query.set("period_end", filters.periodEnd);
  if (filters.country) query.set("country", filters.country);
  if (filters.model) query.set("model", filters.model);
  if (filters.conversionState) query.set("conversion_state", filters.conversionState);
  if (filters.sellOutState) query.set("sell_out_state", filters.sellOutState);
  if (filters.unresolvedOnly) query.set("unresolved_only", "true");
  return apiClient.get<JoybuyOperationCommercialDTO>(`/api/neo/joybuy-operation-commercial${query.size ? `?${query}` : ""}`, { signal, timeoutMs: 30_000 });
};
export const getNeoPaymentAuthority = (signal?: AbortSignal) => apiClient.get<NeoPaymentAuthorityDTO>("/api/neo/payment-authority", { signal });
export const getNeoDataAuthority = (year: number, asOfDate: string, signal?: AbortSignal) => apiClient.get<NeoDataAuthorityDTO>(`/api/neo/data-authority?year=${year}&as_of_date=${encodeURIComponent(asOfDate)}`, { signal, timeoutMs: 60_000 });
export const getNeoJoybuyPromotion = (month: string, signal?: AbortSignal) => apiClient.get<NeoJoybuyPromotionDTO>(`/api/neo/joybuy-order-planning/context?business_month=${encodeURIComponent(month)}`, { signal, timeoutMs: 8_000 });
export const getNeoPromotionProposal = (asOfDate: string, proposalId?: string, signal?: AbortSignal) => {
  const query = new URLSearchParams({ as_of_date: asOfDate }); if (proposalId) query.set("proposal_id", proposalId);
  return apiClient.get<NeoPromotionProposalDTO>(`/api/neo/promotion/proposal-context?${query}`, { signal, timeoutMs: 30_000 });
};

export async function exportNeoJoybuyPromotion(month: string, scenarios: Record<string, Record<string, string>>) {
  return apiClient.download("/api/neo/joybuy-order-planning/export", {
    method: "POST",
    timeoutMs: 30_000,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ business_month: month, scenarios }),
  });
}
